import React from 'react';
import { useQuery } from '@apollo/client';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Button } from '@/components/ui/button';
import { toast } from '@/components/ui/toast';
import { PermissionManager, PermissionPresetSelect } from './permissionManager';
import { UserSecurityPermissionsManager } from './UserSecurityPermissionsManager';
import { LIST_CREATE_USER_ACCOUNT_OPTIONS, LIST_POLICY_ALIASES } from '../queries';
import { findMatchingOrganizationalAlias } from '../organizationalAliases';
import {
  mapActiveOptionLabels,
  sortRequestOptionsAlphabetically
} from '@/pages/admin/userAccountRequests/optionSorting';
import {
  applyPolicyBackedPermissionAccessPolicies,
  getPoliciesForPolicyBackedPermissionAccess,
  isPolicyBackedPermissionAccess
} from '@/utils/userPermissionAccessPolicies';
import { USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES } from '../userManagerPermissionTemplates';

type CreateUserTwoFactorMethod = 'email' | 'sso';
type LegacyPermissionState = {
  permission: string;
  enabled: boolean;
};

type UserAccountRequestOption = {
  id?: number | null;
  optionType?: string | null;
  label?: string | null;
  sortOrder?: number | null;
  isActive?: boolean | null;
};

type CreateUserAccountOptionsData = {
  roleOptions?: UserAccountRequestOption[] | null;
  organizationOptions?: UserAccountRequestOption[] | null;
  siteOptions?: UserAccountRequestOption[] | null;
};

type PolicyAliasRecord = {
  alias: string;
  type?: string | null;
  policies?: string[] | null;
};

export type CreateUserDialogValues = {
  username: string;
  realname: string;
  emailAddress: string;
  organization: string;
  site: string;
  roleName: string;
  twoFactorMethod: CreateUserTwoFactorMethod;
  legacyAccesses: string[];
  selectedPolicies: string[];
};

type CreateUserDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (values: CreateUserDialogValues) => Promise<void> | void;
  title?: string;
  description?: string;
  submitLabel?: string;
  submitting?: boolean;
  initialValues?: Partial<CreateUserDialogValues>;
  showRealNameField?: boolean;
  contextPanel?: React.ReactNode;
};

const normalizePolicies = (values?: string[]) => {
  const normalized = (values || [])
    .map((item) => `${item || ''}`.trim())
    .filter((item) => item.length > 0);
  return Array.from(new Set(normalized));
};

const normalizePolicyKey = (value: unknown) => `${value || ''}`.trim().toLowerCase();
const normalizeOptionValue = (value?: string | null) => `${value || ''}`.trim();
const addCurrentValueToOptions = (options: string[], currentValue?: string) => {
  const normalizedCurrentValue = normalizeOptionValue(currentValue);
  if (!normalizedCurrentValue) {
    return options;
  }

  const hasCurrentValue = options.some(
    (option) => option.toLowerCase() === normalizedCurrentValue.toLowerCase()
  );
  return hasCurrentValue ? options : [...options, normalizedCurrentValue].sort((left, right) => left.localeCompare(right));
};

const DHS_SSO_DOMAIN = 'dhs.lacounty.gov';
const normalizeEmailAddress = (value?: string) => `${value || ''}`.trim().toLowerCase();
const usernameFromEmailAddress = (value?: string) => normalizeEmailAddress(value);
const isDhsSsoEmailAddress = (emailAddress?: string) => normalizeEmailAddress(emailAddress).endsWith(`@${DHS_SSO_DOMAIN}`);
const normalizeCreateTwoFactorMethod = (
  value?: string,
  emailAddress?: string
): CreateUserTwoFactorMethod => {
  if (isDhsSsoEmailAddress(emailAddress)) {
    return 'sso';
  }
  const normalized = `${value || ''}`.trim().toLowerCase();
  if (normalized === 'sso') {
    return 'sso';
  }
  return 'email';
};

const simpleManagedValues = USER_MANAGER_PERMISSION_TEMPLATE_MANAGED_VALUES;
const simpleManagedPolicyValues = Array.from(new Set(
  simpleManagedValues.flatMap((value) => {
    if (isPolicyBackedPermissionAccess(value)) {
      return getPoliciesForPolicyBackedPermissionAccess(value);
    }
    return [value];
  })
)).map((value) => normalizePolicyKey(value));

const defaultValues: CreateUserDialogValues = {
  username: '',
  realname: '',
  emailAddress: '',
  organization: '',
  site: '',
  roleName: '',
  twoFactorMethod: 'email',
  legacyAccesses: [],
  selectedPolicies: []
};

const buildInitialValues = (initialValues?: Partial<CreateUserDialogValues>): CreateUserDialogValues => {
  const merged = { ...defaultValues, ...(initialValues || {}) };
  const emailAddress = normalizeEmailAddress(merged.emailAddress);
  return {
    ...merged,
    username: usernameFromEmailAddress(emailAddress),
    emailAddress,
    twoFactorMethod: normalizeCreateTwoFactorMethod(merged.twoFactorMethod, emailAddress),
    legacyAccesses: normalizePolicies(merged.legacyAccesses),
    selectedPolicies: normalizePolicies(merged.selectedPolicies)
  };
};

export const CreateUserDialog: React.FC<CreateUserDialogProps> = ({
  open,
  onOpenChange,
  onSubmit,
  title = 'Add a new User',
  description = 'To add a new person, fill in the fields below.',
  submitLabel = 'Add',
  submitting = false,
  initialValues,
  showRealNameField = true,
  contextPanel
}) => {
  const [form, setForm] = React.useState<CreateUserDialogValues>(buildInitialValues(initialValues));
  const autoRolePolicyKeysRef = React.useRef<Set<string>>(new Set());
  const { data: optionData, loading: optionsLoading } = useQuery<CreateUserAccountOptionsData>(
    LIST_CREATE_USER_ACCOUNT_OPTIONS,
    { fetchPolicy: 'cache-and-network', skip: !open }
  );
  const { data: aliasData } = useQuery<{ listPolicyAliases: PolicyAliasRecord[] }>(
    LIST_POLICY_ALIASES,
    { fetchPolicy: 'cache-and-network', skip: !open }
  );

  React.useEffect(() => {
    if (!open) return;
    autoRolePolicyKeysRef.current = new Set();
    setForm(buildInitialValues(initialValues));
  }, [open, initialValues]);

  const organizationOptions = React.useMemo(
    () => addCurrentValueToOptions(
      mapActiveOptionLabels(optionData?.organizationOptions, sortRequestOptionsAlphabetically),
      form.organization
    ),
    [form.organization, optionData?.organizationOptions]
  );
  const siteOptions = React.useMemo(
    () => addCurrentValueToOptions(
      mapActiveOptionLabels(optionData?.siteOptions, sortRequestOptionsAlphabetically),
      form.site
    ),
    [form.site, optionData?.siteOptions]
  );
  const roleOptions = React.useMemo(
    () => addCurrentValueToOptions(
      mapActiveOptionLabels(optionData?.roleOptions, sortRequestOptionsAlphabetically),
      form.roleName
    ),
    [form.roleName, optionData?.roleOptions]
  );

  React.useEffect(() => {
    if (!open) return;
    const matchedAlias = findMatchingOrganizationalAlias(
      aliasData?.listPolicyAliases || [],
      form.organization,
      form.roleName
    );
    const nextRolePolicies = normalizePolicies(matchedAlias?.policies || []);
    const nextRolePolicyKeys = new Set(nextRolePolicies.map((policy) => normalizePolicyKey(policy)));
    const previousRolePolicyKeys = autoRolePolicyKeysRef.current;

    autoRolePolicyKeysRef.current = nextRolePolicyKeys;
    setForm((prev) => {
      const withoutPreviousRolePolicies = normalizePolicies(prev.selectedPolicies)
        .filter((policy) => !previousRolePolicyKeys.has(normalizePolicyKey(policy)));
      const nextSelectedPolicies = normalizePolicies([
        ...withoutPreviousRolePolicies,
        ...nextRolePolicies
      ]);
      const previousKey = normalizePolicies(prev.selectedPolicies).map((policy) => normalizePolicyKey(policy)).join('|');
      const nextKey = nextSelectedPolicies.map((policy) => normalizePolicyKey(policy)).join('|');
      if (previousKey === nextKey) {
        return prev;
      }
      return {
        ...prev,
        selectedPolicies: nextSelectedPolicies
      };
    });
  }, [aliasData?.listPolicyAliases, form.organization, form.roleName, open]);

  const setField = <K extends keyof CreateUserDialogValues>(key: K, value: CreateUserDialogValues[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const handleEmailAddressChange = (nextEmailAddress: string) => {
    setForm((prev) => {
      const normalizedEmailAddress = normalizeEmailAddress(nextEmailAddress);
      const nextTwoFactorMethod = isDhsSsoEmailAddress(normalizedEmailAddress)
        ? 'sso'
        : prev.twoFactorMethod;
      return {
        ...prev,
        emailAddress: nextEmailAddress,
        twoFactorMethod: nextTwoFactorMethod
      };
    });
  };

  const simplePermissionStates = React.useMemo<LegacyPermissionState[]>(() => {
    const assignedPolicySet = new Set(normalizePolicies(form.selectedPolicies).map((policy) => normalizePolicyKey(policy)));
    return simpleManagedValues.map((permission) => {
      const requiredPolicies = isPolicyBackedPermissionAccess(permission)
        ? getPoliciesForPolicyBackedPermissionAccess(permission)
        : [permission];
      return {
        permission,
        enabled: requiredPolicies.length > 0 &&
          requiredPolicies.every((policy) => assignedPolicySet.has(normalizePolicyKey(policy)))
      };
    });
  }, [form.selectedPolicies]);

  const handleAssignedPoliciesChange = React.useCallback((nextPolicies: string[]) => {
    setForm((prev) => ({
      ...prev,
      selectedPolicies: normalizePolicies(nextPolicies)
    }));
  }, []);

  const handleSimplePermissionsChange = React.useCallback((nextPermissions: LegacyPermissionState[]) => {
    const enabledManagedValues = normalizePolicies(
      (Array.isArray(nextPermissions) ? nextPermissions : [])
        .filter((permissionState) => permissionState?.enabled !== false)
        .map((permissionState) => `${permissionState?.permission || ''}`.trim())
        .filter(Boolean)
    );
    const enabledPolicyBackedAccesses = enabledManagedValues.filter((permissionName) =>
      isPolicyBackedPermissionAccess(permissionName)
    );
    const enabledDirectGroups = enabledManagedValues.filter((permissionName) =>
      !isPolicyBackedPermissionAccess(permissionName)
    );

    setForm((prev) => {
      const preservedPolicies = normalizePolicies(prev.selectedPolicies).filter((policy) =>
        !simpleManagedPolicyValues.includes(normalizePolicyKey(policy))
      );
      const nextSimplePolicies = applyPolicyBackedPermissionAccessPolicies(
        enabledDirectGroups,
        enabledPolicyBackedAccesses,
        simpleManagedValues.filter((permissionName) => isPolicyBackedPermissionAccess(permissionName))
      );

      return {
        ...prev,
        legacyAccesses: [],
        selectedPolicies: normalizePolicies([
          ...preservedPolicies,
          ...nextSimplePolicies
        ])
      };
    });
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const normalizedEmailAddress = normalizeEmailAddress(form.emailAddress);
    const normalizedPolicies = normalizePolicies(form.selectedPolicies);
    const normalized: CreateUserDialogValues = {
      username: usernameFromEmailAddress(normalizedEmailAddress),
      realname: form.realname.trim(),
      emailAddress: normalizedEmailAddress,
      organization: form.organization.trim(),
      site: form.site.trim(),
      roleName: form.roleName.trim(),
      twoFactorMethod: normalizeCreateTwoFactorMethod(form.twoFactorMethod, normalizedEmailAddress),
      legacyAccesses: [],
      selectedPolicies: normalizedPolicies
    };

    if (showRealNameField && !normalized.realname) {
      toast.error('Name is required');
      return;
    }

    if (!normalized.emailAddress) {
      toast.error('Email address is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalized.emailAddress)) {
      toast.error('Email address is invalid');
      return;
    }

    await onSubmit(normalized);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        contentWidth="1300px"
        contentHeight="auto"
        contentMaxWidth="98vw"
        contentMaxHeight="94vh"
        className="overflow-y-auto rounded-lg"
      >
        <DialogHeader className="space-y-1">
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        <form className="space-y-3" onSubmit={handleSubmit}>
          {contextPanel}

          <div className="rounded-md border border-gray-200 bg-white p-3">
            <div className={`grid gap-3 ${showRealNameField ? 'md:grid-cols-3' : 'md:grid-cols-4'}`}>
              {showRealNameField && (
                <div className="space-y-1">
                  <label htmlFor="create-user-realname" className="block text-xs font-semibold text-gray-900">
                    First and last name
                  </label>
                  <Input
                    id="create-user-realname"
                    value={form.realname}
                    onChange={(event) => setField('realname', event.target.value)}
                    maxLength={30}
                  />
                </div>
              )}

              <div className="space-y-1">
                <label htmlFor="create-user-email" className="block text-xs font-semibold text-gray-900">
                  Email
                </label>
                <Input
                  id="create-user-email"
                  type="email"
                  value={form.emailAddress}
                  onChange={(event) => handleEmailAddressChange(event.target.value)}
                  maxLength={40}
                />
              </div>

              <div className="space-y-1">
                <label className="block text-xs font-semibold text-gray-900">Sign-in method</label>
                <Select
                  value={form.twoFactorMethod}
                  onValueChange={(value) =>
                    setField('twoFactorMethod', normalizeCreateTwoFactorMethod(value, form.emailAddress))
                  }
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="email">Username/password + TOTP setup</SelectItem>
                    <SelectItem value="sso">SSO (Azure AD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

	              <div className="space-y-1">
	                <label className="block text-xs font-semibold text-gray-900">
	                  Organization
	                </label>
	                <Select
	                  value={form.organization || '__none'}
	                  onValueChange={(value) => setField('organization', value === '__none' ? '' : value)}
	                  disabled={optionsLoading || organizationOptions.length === 0}
	                >
	                  <SelectTrigger id="create-user-organization" className="w-full">
	                    <SelectValue placeholder={optionsLoading ? 'Loading organizations...' : 'Select an organization'} />
	                  </SelectTrigger>
	                  <SelectContent>
	                    <SelectItem value="__none">
	                      {optionsLoading ? 'Loading organizations...' : 'Select an organization'}
	                    </SelectItem>
	                    {organizationOptions.map((organization) => (
	                      <SelectItem key={organization} value={organization}>
	                        {organization}
	                      </SelectItem>
	                    ))}
	                  </SelectContent>
	                </Select>
	              </div>

	              <div className="space-y-1">
	                <label className="block text-xs font-semibold text-gray-900">
	                  Site
	                </label>
	                <Select
	                  value={form.site || '__none'}
	                  onValueChange={(value) => setField('site', value === '__none' ? '' : value)}
	                  disabled={optionsLoading || siteOptions.length === 0}
	                >
	                  <SelectTrigger id="create-user-site" className="w-full">
	                    <SelectValue placeholder={optionsLoading ? 'Loading sites...' : 'No site assigned'} />
	                  </SelectTrigger>
	                  <SelectContent>
	                    <SelectItem value="__none">
	                      {optionsLoading ? 'Loading sites...' : 'No site assigned'}
	                    </SelectItem>
	                    {siteOptions.map((site) => (
	                      <SelectItem key={site} value={site}>
	                        {site}
	                      </SelectItem>
	                    ))}
	                  </SelectContent>
	                </Select>
	              </div>

	              <div className="space-y-1">
	                <label className="block text-xs font-semibold text-gray-900">
	                  User role
	                </label>
	                <Select
	                  value={form.roleName || '__none'}
	                  onValueChange={(value) => setField('roleName', value === '__none' ? '' : value)}
	                  disabled={optionsLoading || roleOptions.length === 0}
	                >
	                  <SelectTrigger id="create-user-role" className="w-full">
	                    <SelectValue placeholder={optionsLoading ? 'Loading roles...' : 'Select a user role'} />
	                  </SelectTrigger>
	                  <SelectContent>
	                    <SelectItem value="__none">
	                      {optionsLoading ? 'Loading roles...' : 'Select a user role'}
	                    </SelectItem>
	                    {roleOptions.map((role) => (
	                      <SelectItem key={role} value={role}>
	                        {role}
	                      </SelectItem>
	                    ))}
	                  </SelectContent>
	                </Select>
	              </div>
            </div>
          </div>

          <UserSecurityPermissionsManager
            username=""
            assignedPolicies={form.selectedPolicies}
            onChangeAssignedPolicies={handleAssignedPoliciesChange}
            tabsPosition="right"
            headerContent={
              <p className="text-sm font-semibold text-gray-900">Permissions</p>
            }
            headerActions={
              <PermissionPresetSelect
                selectedPermissions={simplePermissionStates}
                onChange={handleSimplePermissionsChange}
              />
            }
            simplePermissions={
              <PermissionManager
                selectedPermissions={simplePermissionStates}
                onChange={handleSimplePermissionsChange}
                hidePresetSelector
              />
            }
          />

          <DialogFooter className="gap-2">
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Saving...' : submitLabel}
            </Button>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default CreateUserDialog;
